import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { scrollIntoView } from '@theme/scrolling';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        if (newMediaGallery instanceof MediaGallery) {
          newMediaGallery.#scrollToSelectedMedia();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * In grid presentation, the gallery keeps a fixed image order (it doesn't reorder to put the
   * variant's image first), so on variant change we scroll to that image instead. On desktop
   * that means scrolling the grid tile into view; below 750px the grid is hidden in favour of
   * the carousel (see product-media-gallery-content-styles), so we scroll that carousel to the
   * matching slide instead.
   */
  #scrollToSelectedMedia() {
    if (this.presentation !== 'grid') return;

    const { selectedMediaId } = this.dataset;
    if (!selectedMediaId) return;

    if (window.matchMedia('(min-width: 750px)').matches) {
      const target = this.querySelector(`.media-gallery__grid [data-media-id="${selectedMediaId}"]`);
      if (!target) return;

      scrollIntoView(target, { behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
      return;
    }

    // animate: false is required, not just a style choice: Slideshow#select's animated path
    // reorders the DOM to fake a short adjacent-slide scroll for any non-adjacent jump, and
    // sets `current` (and the pagination text) from that transient position instead of the
    // real target index — it never gets corrected once the slide is restored afterwards. A
    // colour swap can jump many slides at once, so we bypass that path entirely.
    this.slideshow?.select({ id: selectedMediaId }, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
